import React, { useState, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, CloudArrowUpIcon } from '@heroicons/react/24/outline';
import { apiClient } from '../../api/apiConfig';
import { AxiosProgressEvent } from 'axios';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../../design/tokens';

interface LessonFormProps {
  isOpen: boolean;
  onClose: () => void;
  onLessonCreated: () => void;
  moduleId: number;
}

interface LessonFormData {
  title: string;
  video_url: string;
  video_file?: File;
  transcript: string;
  summary: string;
  personal_notes: string;
  quiz: string;
  order: number;
  is_completed: boolean;
}

const LessonForm: React.FC<LessonFormProps> = ({ isOpen, onClose, onLessonCreated, moduleId }) => {
  const [formData, setFormData] = useState<LessonFormData>({
    title: '',
    video_url: '',
    transcript: '',
    summary: '',
    personal_notes: '',
    quiz: '',
    order: 1,
    is_completed: false,
  });

  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Check file size (max 10GB)
      if (file.size > 10 * 1024 * 1024 * 1024) {
        setError('Video file size must be less than 10GB');
        return;
      }

      // Check file type
      const validTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/x-ms-wmv', 'video/x-flv', 'video/webm'];
      if (!validTypes.includes(file.type)) {
        setError('Invalid video format. Supported formats are: MP4, MOV, AVI, WMV, FLV, WEBM');
        return;
      }

      setFormData(prev => ({ ...prev, video_file: file }));
      setError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      setIsUploading(true);
      setUploadProgress(0);

      // Create FormData for file upload
      const formDataToSend = new FormData();
      formDataToSend.append('module_id', moduleId.toString());
      formDataToSend.append('title', formData.title);
      formDataToSend.append('transcript', formData.transcript);
      formDataToSend.append('summary', formData.summary);
      formDataToSend.append('personal_notes', formData.personal_notes);
      formDataToSend.append('quiz', formData.quiz);
      formDataToSend.append('order', formData.order.toString());
      formDataToSend.append('is_completed', formData.is_completed.toString());

      // If there's a video file, append it
      if (formData.video_file) {
        formDataToSend.append('video_file', formData.video_file);
      } else if (formData.video_url) {
        formDataToSend.append('video_url', formData.video_url);
      }

      // Send the request with upload progress tracking
      const response = await apiClient.post('/api/lessons/', formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent: AxiosProgressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percentCompleted);
          }
        },
        // Increase timeout for large files
        timeout: 300000, // 5 minutes
      });

      onLessonCreated();
      onClose();
      setFormData({
        title: '',
        video_url: '',
        transcript: '',
        summary: '',
        personal_notes: '',
        quiz: '',
        order: 1,
        is_completed: false,
      });
      setUploadProgress(0);
    } catch (error: any) {
      console.error('Error creating lesson:', error);
      setError(
        error.response?.data?.detail ||
        error.response?.data?.error ||
        error.message ||
        'Failed to create lesson'
      );
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Transition show={isOpen} as={React.Fragment}>
      <Dialog as="div" style={{ position: 'relative', zIndex: 10 }} onClose={onClose}>
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(107, 114, 128, 0.75)',
            transition: 'opacity'
          }} />
        </Transition.Child>

        <div style={{
          position: 'fixed',
          inset: 0,
          zIndex: 10,
          overflowY: 'auto'
        }}>
          <div style={{
            display: 'flex',
            minHeight: '100%',
            alignItems: 'center',
            justifyContent: 'center',
            padding: SPACING[16],
            textAlign: 'center'
          }}>
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel style={{
                position: 'relative',
                transform: 'none',
                overflow: 'hidden',
                borderRadius: RADIUS,
                backgroundColor: COLORS.bg.panel,
                padding: SPACING[24],
                textAlign: 'left',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                transition: 'all',
                width: '100%',
                maxWidth: '512px',
                margin: `${SPACING[32]} 0`
              }}>
                <div style={{
                  position: 'absolute',
                  right: 0,
                  top: 0,
                  paddingRight: SPACING[16],
                  paddingTop: SPACING[16]
                }}>
                  <button
                    type="button"
                    style={{
                      borderRadius: RADIUS,
                      backgroundColor: COLORS.bg.panel,
                      color: COLORS.text.tertiary,
                      border: 'none',
                      cursor: 'pointer',
                      padding: SPACING[4]
                    }}
                    onClick={onClose}
                  >
                    <span style={{
                      position: 'absolute',
                      width: '1px',
                      height: '1px',
                      padding: 0,
                      margin: '-1px',
                      overflow: 'hidden',
                      clip: 'rect(0, 0, 0, 0)',
                      whiteSpace: 'nowrap',
                      border: 0
                    }}>Close</span>
                    <XMarkIcon style={{ height: '24px', width: '24px' }} aria-hidden="true" />
                  </button>
                </div>

                <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                  <div style={{ marginTop: 0, textAlign: 'left', width: '100%' }}>
                    <Dialog.Title as="h3" style={{
                      fontSize: TYPOGRAPHY.headings.sizes.h2,
                      fontFamily: TYPOGRAPHY.headings.fontFamily,
                      fontWeight: TYPOGRAPHY.weight.semibold,
                      lineHeight: '1.5',
                      color: COLORS.text.primary
                    }}>
                      Add New Lesson
                    </Dialog.Title>

                    {error && (
                      <div style={{
                        marginTop: SPACING[8],
                        borderRadius: RADIUS,
                        backgroundColor: '#FEF2F2',
                        padding: SPACING[16]
                      }}>
                        <div style={{ display: 'flex' }}>
                          <div style={{ marginLeft: SPACING[12] }}>
                            <h3 style={{
                              fontSize: TYPOGRAPHY.body.sizes.default,
                              fontFamily: TYPOGRAPHY.body.fontFamily,
                              fontWeight: TYPOGRAPHY.weight.medium,
                              color: '#991B1B'
                            }}>Error</h3>
                            <div style={{
                              marginTop: SPACING[8],
                              fontSize: TYPOGRAPHY.body.sizes.default,
                              fontFamily: TYPOGRAPHY.body.fontFamily,
                              color: '#B91C1C'
                            }}>{error}</div>
                          </div>
                        </div>
                      </div>
                    )}

                    <form onSubmit={handleSubmit} style={{ marginTop: SPACING[16] }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
                        <div>
                          <label htmlFor="title" style={{
                            display: 'block',
                            fontSize: TYPOGRAPHY.body.sizes.default,
                            fontFamily: TYPOGRAPHY.body.fontFamily,
                            fontWeight: TYPOGRAPHY.weight.medium,
                            color: COLORS.text.secondary
                          }}>
                            Title
                          </label>
                          <input
                            type="text"
                            id="title"
                            value={formData.title}
                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                            style={{
                              marginTop: SPACING[4],
                              display: 'block',
                              width: '100%',
                              borderRadius: RADIUS,
                              border: `1px solid ${COLORS.border.default}`,
                              padding: `${SPACING[8]} ${SPACING[12]}`,
                              fontSize: TYPOGRAPHY.body.sizes.default,
                              fontFamily: TYPOGRAPHY.body.fontFamily,
                              color: COLORS.text.primary,
                              backgroundColor: COLORS.bg.panel,
                              outline: 'none',
                              boxSizing: 'border-box'
                            }}
                            required
                          />
                        </div>

                        <div>
                          <label htmlFor="video_url" style={{
                            display: 'block',
                            fontSize: TYPOGRAPHY.body.sizes.default,
                            fontFamily: TYPOGRAPHY.body.fontFamily,
                            fontWeight: TYPOGRAPHY.weight.medium,
                            color: COLORS.text.secondary
                          }}>
                            Video URL (for external videos)
                          </label>
                          <div style={{ marginTop: SPACING[4] }}>
                            <input
                              type="url"
                              id="video_url"
                              value={formData.video_url}
                              onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                              style={{
                                display: 'block',
                                width: '100%',
                                borderRadius: RADIUS,
                                border: `1px solid ${COLORS.border.default}`,
                                padding: `${SPACING[8]} ${SPACING[12]}`,
                                fontSize: TYPOGRAPHY.body.sizes.default,
                                fontFamily: TYPOGRAPHY.body.fontFamily,
                                color: COLORS.text.primary,
                                backgroundColor: COLORS.bg.panel,
                                outline: 'none',
                                boxSizing: 'border-box'
                              }}
                              placeholder="https://example.com/video.mp4"
                            />
                            <p style={{
                              marginTop: SPACING[4],
                              fontSize: TYPOGRAPHY.body.sizes.default,
                              fontFamily: TYPOGRAPHY.body.fontFamily,
                              color: COLORS.text.tertiary
                            }}>
                              Use this for linking to external videos. For uploading your own video, use the upload option below.
                            </p>
                          </div>
                        </div>

                        <div style={{
                          borderTop: `1px solid ${COLORS.border.default}`,
                          paddingTop: SPACING[16]
                        }}>
                          <label style={{
                            display: 'block',
                            fontSize: TYPOGRAPHY.body.sizes.default,
                            fontFamily: TYPOGRAPHY.body.fontFamily,
                            fontWeight: TYPOGRAPHY.weight.medium,
                            color: COLORS.text.secondary
                          }}>
                            Upload Video
                          </label>
                          <div style={{ marginTop: SPACING[4] }}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                              <input
                                type="file"
                                id="video_file"
                                ref={fileInputRef}
                                onChange={handleFileChange}
                                accept="video/*"
                                style={{ display: 'none' }}
                              />
                              <button
                                type="button"
                                onClick={() => fileInputRef.current?.click()}
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  padding: `${SPACING[8]} ${SPACING[16]}`,
                                  border: `1px solid ${COLORS.border.default}`,
                                  fontSize: TYPOGRAPHY.body.sizes.default,
                                  fontFamily: TYPOGRAPHY.body.fontFamily,
                                  fontWeight: TYPOGRAPHY.weight.medium,
                                  borderRadius: RADIUS,
                                  color: COLORS.text.secondary,
                                  backgroundColor: COLORS.bg.panel,
                                  cursor: isUploading ? 'not-allowed' : 'pointer',
                                  opacity: isUploading ? 0.5 : 1
                                }}
                                disabled={isUploading}
                              >
                                <CloudArrowUpIcon style={{ height: '20px', width: '20px', marginRight: SPACING[8] }} />
                                {formData.video_file ? 'Change Video File' : 'Upload Video File'}
                              </button>
                              {formData.video_file && (
                                <>
                                  <span style={{
                                    marginLeft: SPACING[12],
                                    fontSize: TYPOGRAPHY.body.sizes.default,
                                    fontFamily: TYPOGRAPHY.body.fontFamily,
                                    color: COLORS.text.tertiary
                                  }}>
                                    Selected: {formData.video_file.name}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setFormData(prev => ({ ...prev, video_file: undefined }));
                                      if (fileInputRef.current) {
                                        fileInputRef.current.value = '';
                                      }
                                    }}
                                    style={{
                                      marginLeft: SPACING[8],
                                      fontSize: TYPOGRAPHY.body.sizes.default,
                                      fontFamily: TYPOGRAPHY.body.fontFamily,
                                      color: COLORS.status.error,
                                      background: 'none',
                                      border: 'none',
                                      cursor: 'pointer'
                                    }}
                                  >
                                    Remove
                                  </button>
                                </>
                              )}
                            </div>
                            <p style={{
                              marginTop: SPACING[4],
                              fontSize: TYPOGRAPHY.body.sizes.default,
                              fontFamily: TYPOGRAPHY.body.fontFamily,
                              color: COLORS.text.tertiary
                            }}>
                              Upload your own video file. Supported formats: MP4, MOV, AVI, WMV, FLV, WEBM. Max size: 10GB.
                            </p>
                            {isUploading && (
                              <div style={{ marginTop: SPACING[8] }}>
                                <div style={{ position: 'relative', paddingTop: SPACING[4] }}>
                                  <div style={{
                                    overflow: 'hidden',
                                    height: '8px',
                                    display: 'flex',
                                    borderRadius: RADIUS,
                                    backgroundColor: '#DBEAFE'
                                  }}>
                                    <div
                                      style={{
                                        width: `${uploadProgress}%`,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        textAlign: 'center',
                                        whiteSpace: 'nowrap',
                                        color: COLORS.bg.panel,
                                        justifyContent: 'center',
                                        backgroundColor: COLORS.accent.default,
                                        transition: 'width 0.3s ease'
                                      }}
                                    />
                                  </div>
                                  <div style={{
                                    fontSize: TYPOGRAPHY.body.sizes.small,
                                    fontFamily: TYPOGRAPHY.body.fontFamily,
                                    color: COLORS.text.tertiary,
                                    marginTop: SPACING[4]
                                  }}>
                                    {uploadProgress}% uploaded
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        <div>
                          <label htmlFor="transcript" style={{
                            display: 'block',
                            fontSize: TYPOGRAPHY.body.sizes.default,
                            fontFamily: TYPOGRAPHY.body.fontFamily,
                            fontWeight: TYPOGRAPHY.weight.medium,
                            color: COLORS.text.secondary
                          }}>
                            Transcript
                          </label>
                          <textarea
                            id="transcript"
                            value={formData.transcript}
                            onChange={(e) => setFormData({ ...formData, transcript: e.target.value })}
                            rows={3}
                            style={{
                              marginTop: SPACING[4],
                              display: 'block',
                              width: '100%',
                              borderRadius: RADIUS,
                              border: `1px solid ${COLORS.border.default}`,
                              padding: `${SPACING[8]} ${SPACING[12]}`,
                              fontSize: TYPOGRAPHY.body.sizes.default,
                              fontFamily: TYPOGRAPHY.body.fontFamily,
                              color: COLORS.text.primary,
                              backgroundColor: COLORS.bg.panel,
                              outline: 'none',
                              resize: 'vertical',
                              boxSizing: 'border-box'
                            }}
                          />
                        </div>

                        <div>
                          <label htmlFor="summary" style={{
                            display: 'block',
                            fontSize: TYPOGRAPHY.body.sizes.default,
                            fontFamily: TYPOGRAPHY.body.fontFamily,
                            fontWeight: TYPOGRAPHY.weight.medium,
                            color: COLORS.text.secondary
                          }}>
                            Summary
                          </label>
                          <textarea
                            id="summary"
                            value={formData.summary}
                            onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
                            rows={3}
                            style={{
                              marginTop: SPACING[4],
                              display: 'block',
                              width: '100%',
                              borderRadius: RADIUS,
                              border: `1px solid ${COLORS.border.default}`,
                              padding: `${SPACING[8]} ${SPACING[12]}`,
                              fontSize: TYPOGRAPHY.body.sizes.default,
                              fontFamily: TYPOGRAPHY.body.fontFamily,
                              color: COLORS.text.primary,
                              backgroundColor: COLORS.bg.panel,
                              outline: 'none',
                              resize: 'vertical',
                              boxSizing: 'border-box'
                            }}
                          />
                        </div>

                        <div>
                          <label htmlFor="personal_notes" style={{
                            display: 'block',
                            fontSize: TYPOGRAPHY.body.sizes.default,
                            fontFamily: TYPOGRAPHY.body.fontFamily,
                            fontWeight: TYPOGRAPHY.weight.medium,
                            color: COLORS.text.secondary
                          }}>
                            Personal Notes
                          </label>
                          <textarea
                            id="personal_notes"
                            value={formData.personal_notes}
                            onChange={(e) => setFormData({ ...formData, personal_notes: e.target.value })}
                            rows={3}
                            style={{
                              marginTop: SPACING[4],
                              display: 'block',
                              width: '100%',
                              borderRadius: RADIUS,
                              border: `1px solid ${COLORS.border.default}`,
                              padding: `${SPACING[8]} ${SPACING[12]}`,
                              fontSize: TYPOGRAPHY.body.sizes.default,
                              fontFamily: TYPOGRAPHY.body.fontFamily,
                              color: COLORS.text.primary,
                              backgroundColor: COLORS.bg.panel,
                              outline: 'none',
                              resize: 'vertical',
                              boxSizing: 'border-box'
                            }}
                          />
                        </div>

                        <div>
                          <label htmlFor="quiz" style={{
                            display: 'block',
                            fontSize: TYPOGRAPHY.body.sizes.default,
                            fontFamily: TYPOGRAPHY.body.fontFamily,
                            fontWeight: TYPOGRAPHY.weight.medium,
                            color: COLORS.text.secondary
                          }}>
                            Quiz
                          </label>
                          <textarea
                            id="quiz"
                            value={formData.quiz}
                            onChange={(e) => setFormData({ ...formData, quiz: e.target.value })}
                            rows={3}
                            style={{
                              marginTop: SPACING[4],
                              display: 'block',
                              width: '100%',
                              borderRadius: RADIUS,
                              border: `1px solid ${COLORS.border.default}`,
                              padding: `${SPACING[8]} ${SPACING[12]}`,
                              fontSize: TYPOGRAPHY.body.sizes.default,
                              fontFamily: TYPOGRAPHY.body.fontFamily,
                              color: COLORS.text.primary,
                              backgroundColor: COLORS.bg.panel,
                              outline: 'none',
                              resize: 'vertical',
                              boxSizing: 'border-box'
                            }}
                          />
                        </div>

                        <div>
                          <label htmlFor="order" style={{
                            display: 'block',
                            fontSize: TYPOGRAPHY.body.sizes.default,
                            fontFamily: TYPOGRAPHY.body.fontFamily,
                            fontWeight: TYPOGRAPHY.weight.medium,
                            color: COLORS.text.secondary
                          }}>
                            Order
                          </label>
                          <input
                            type="number"
                            id="order"
                            value={formData.order}
                            onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                            style={{
                              marginTop: SPACING[4],
                              display: 'block',
                              width: '100%',
                              borderRadius: RADIUS,
                              border: `1px solid ${COLORS.border.default}`,
                              padding: `${SPACING[8]} ${SPACING[12]}`,
                              fontSize: TYPOGRAPHY.body.sizes.default,
                              fontFamily: TYPOGRAPHY.body.fontFamily,
                              color: COLORS.text.primary,
                              backgroundColor: COLORS.bg.panel,
                              outline: 'none',
                              boxSizing: 'border-box'
                            }}
                            min="1"
                            required
                          />
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <input
                            type="checkbox"
                            id="is_completed"
                            checked={formData.is_completed}
                            onChange={(e) => setFormData({ ...formData, is_completed: e.target.checked })}
                            style={{
                              height: '16px',
                              width: '16px',
                              borderRadius: '4px',
                              border: `1px solid ${COLORS.border.default}`,
                              accentColor: COLORS.accent.default
                            }}
                          />
                          <label htmlFor="is_completed" style={{
                            marginLeft: SPACING[8],
                            display: 'block',
                            fontSize: TYPOGRAPHY.body.sizes.default,
                            fontFamily: TYPOGRAPHY.body.fontFamily,
                            color: COLORS.text.secondary
                          }}>
                            Mark as completed
                          </label>
                        </div>

                        <div style={{
                          marginTop: SPACING[24],
                          display: 'flex',
                          flexDirection: 'row-reverse',
                          gap: SPACING[12]
                        }}>
                          <button
                            type="submit"
                            disabled={isUploading}
                            style={{
                              display: 'inline-flex',
                              justifyContent: 'center',
                              borderRadius: RADIUS,
                              padding: `${SPACING[8]} ${SPACING[12]}`,
                              fontSize: TYPOGRAPHY.body.sizes.default,
                              fontFamily: TYPOGRAPHY.body.fontFamily,
                              fontWeight: TYPOGRAPHY.weight.semibold,
                              color: COLORS.bg.panel,
                              backgroundColor: isUploading ? COLORS.text.tertiary : COLORS.accent.default,
                              border: 'none',
                              cursor: isUploading ? 'not-allowed' : 'pointer',
                              boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
                            }}
                          >
                            {isUploading ? 'Uploading...' : 'Add Lesson'}
                          </button>
                          <button
                            type="button"
                            style={{
                              display: 'inline-flex',
                              justifyContent: 'center',
                              borderRadius: RADIUS,
                              backgroundColor: COLORS.bg.panel,
                              padding: `${SPACING[8]} ${SPACING[12]}`,
                              fontSize: TYPOGRAPHY.body.sizes.default,
                              fontFamily: TYPOGRAPHY.body.fontFamily,
                              fontWeight: TYPOGRAPHY.weight.semibold,
                              color: COLORS.text.primary,
                              border: `1px solid ${COLORS.border.default}`,
                              cursor: isUploading ? 'not-allowed' : 'pointer',
                              opacity: isUploading ? 0.5 : 1,
                              boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
                            }}
                            onClick={onClose}
                            disabled={isUploading}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default LessonForm;
